import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCreateGroup } from '@/hooks/useGroups';
import { useSubscription } from '@/hooks/useSubscription';
import { useAuth } from '@/contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { T, RADIUS, SHADOW } from '@/lib/tokens';
import logoOnWhite from '@/assets/logo-on-white.svg';

/**
 *
 */
export default function OnboardingCreateGroup() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const createGroup = useCreateGroup();
  const { maxGroups, isPaid } = useSubscription();
  const [groupName, setGroupName] = useState('');

  // Fire-and-forget: create the Stripe trial subscription for new users.
  // The DB trigger has already granted paid-tier access, so the user isn't
  // blocked if this is slow or fails. This creates the Stripe-side subscription
  // so Stripe can fire customer.subscription.deleted on trial lapse.
  const trialCreated = useRef(false);
  useEffect(() => {
    if (!user || trialCreated.current) return;
    trialCreated.current = true;
    supabase.functions.invoke('create-trial-subscription').catch(() => {
      // Best-effort — user already has DB-level trial access
    });
  }, [user]);

  const { data: groupCount = 0 } = useQuery({
    queryKey: ['my-groups-count', user?.id],
    queryFn: async () => {
      const { count, error } = await supabase
        .from('group_memberships')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user!.id);
      if (error) throw error;
      return count ?? 0;
    },
    enabled: !!user,
  });

  const atGroupLimit = !isPaid && maxGroups > 0 && groupCount >= maxGroups;

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!groupName.trim()) return;
    try {
      const group = await createGroup.mutateAsync({ name: groupName.trim() });
      navigate('/onboarding/add-items', { state: { groupId: group.id, groupName: group.name } });
    } catch {
      // Error toast handled by useCreateGroup onError
    }
  };

  const isDisabled = !groupName.trim() || createGroup.isPending || atGroupLimit;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20,
      }}
    >
      <div style={{ width: '100%', maxWidth: 420, textAlign: 'center' }}>
        {/* Logo */}
        <div style={{ marginBottom: 24 }}>
          <img
            src={logoOnWhite}
            alt="Share Shack"
            style={{ width: 36, height: 36, display: 'block', margin: '0 auto' }}
          />
          <div style={{ fontSize: 15, fontWeight: 600, color: T.text, marginTop: 4 }}>
            Share Shack
          </div>
        </div>

        {/* Step indicator dots */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            marginBottom: 24,
          }}
        >
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              style={{
                height: 8,
                width: s === 1 ? 32 : 8,
                borderRadius: 4,
                background: s === 1 ? T.accent : T.surfaceSubtle,
                transition: 'all 0.2s',
              }}
            />
          ))}
        </div>

        <h1 style={{ fontSize: 22, fontWeight: 600, color: T.text, margin: '0 0 8px' }}>
          Create your sharing group
        </h1>
        <p style={{ fontSize: 14, color: T.textSec, lineHeight: 1.65, margin: '0 0 28px' }}>
          Give your group a name — you can always change it later.
        </p>

        <form onSubmit={handleCreate} style={{ textAlign: 'left' }}>
          <div style={{ marginBottom: 16 }}>
            <Label htmlFor="onboarding-groupName">Group name</Label>
            <Input
              id="onboarding-groupName"
              placeholder="e.g. Family Tools, Book Club"
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              autoFocus
              style={{ marginTop: 6 }}
            />
          </div>
          {atGroupLimit && (
            <p style={{ fontSize: 13, color: T.warning, padding: '4px 0', lineHeight: 1.5, margin: '0 0 8px' }}>
              You've reached your group limit ({groupCount} of {maxGroups}). <span onClick={() => navigate('/account')} style={{ color: T.accent, textDecoration: 'underline', cursor: 'pointer' }}>Upgrade your plan</span> to create more groups.
            </p>
          )}
          <button
            type="submit"
            disabled={isDisabled}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: RADIUS.md,
              background: isDisabled ? T.surfaceSubtle : T.accent,
              color: isDisabled ? T.textMuted : T.white,
              border: isDisabled ? `1px solid ${T.border}` : 'none',
              fontSize: 15,
              fontWeight: 600,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              fontFamily: 'inherit',
              boxShadow: isDisabled ? 'none' : SHADOW.btn.accent,
            }}
          >
            {createGroup.isPending ? 'Creating…' : 'Create Group'}
          </button>
        </form>
      </div>
    </div>
  );
}
